/**
 * Presentation helpers for the lab-research tab of the donor card. The data
 * itself comes from `@entities/donor` (list) and `@entities/lab` (detail).
 */

import { EAnalysisResultType } from '@/entities/analysis-definition'

/** Analysis / admission codes that get a colored badge; anything else is neutral. */
export const LAB_RESEARCH_BADGE_COLORS: Record<
  string,
  { bg: string; color: string } | undefined
> = {
  DONE: { bg: 'badge.green.20', color: 'badge.green.100' },
  CONFIRMED: { bg: 'badge.green.20', color: 'badge.green.100' },
  ADMITTED: { bg: 'badge.green.20', color: 'badge.green.100' },
  ON_DONATION: { bg: 'badge.green.20', color: 'badge.green.100' },
  DEFERRED: { bg: 'badge.red.20', color: 'badge.red.100' },
  REJECTED: { bg: 'badge.red.20', color: 'badge.red.100' },
  RETAKE: { bg: 'badge.red.20', color: 'badge.red.100' },
  IN_PROGRESS: { bg: 'badge.blue.20', color: 'badge.blue.100' },
  IN_CHECK: { bg: 'badge.blue.20', color: 'badge.blue.100' },
}

export const LAB_RESEARCH_NEUTRAL_BADGE = {
  bg: 'badge.gray.20',
  color: 'badge.gray.100',
}

export function getLabResearchBadgeColors(code: string | null | undefined) {
  return (code && LAB_RESEARCH_BADGE_COLORS[code]) || LAB_RESEARCH_NEUTRAL_BADGE
}

/** `< 0.90`, `120 – 160`, `> 4` — same convention as the lab order detail page. */
export function formatLabReference(
  min: number | null,
  max: number | null,
): string {
  if (min != null && max != null) return `${min} – ${max}`
  if (max != null) return `< ${max}`
  if (min != null) return `> ${min}`
  return '—'
}

/**
 * An ENUM result is stored as its value code (`NOT_DETECTED`), which is not
 * something to put in front of a doctor. The definition ships the localized
 * label beside every code, so the value is swapped for its label whenever the
 * parameter carries one; anything else (numbers, free text) is shown verbatim.
 */
export function formatLabResultValue(parameter: {
  value: string | null
  enum_values: { code: string; label: string }[] | null
}): string {
  const value = parameter.value?.trim()
  if (!value) return '—'

  const match = parameter.enum_values?.find(
    (enumValue) => enumValue.code.trim() === value,
  )
  return match?.label || value
}

/**
 * What the result is measured against. Serology (S/CO) is read against a single
 * cutoff and carries no range, so that number is shown in place of a reference.
 */
export function formatLabNorm(parameter: {
  reference_min: number | null
  reference_max: number | null
  cutoff_value: number | null
}): string {
  if (parameter.cutoff_value != null) return String(parameter.cutoff_value)
  return formatLabReference(parameter.reference_min, parameter.reference_max)
}

/**
 * The parameter is answered in prose, so it carries neither a unit nor a norm.
 * ENUM values win over the declared type — a closed set is still a coded
 * answer, not free text.
 */
export function isTextParameter(parameter: {
  result_type: string
  enum_values: { code: string; label: string }[] | null
}): boolean {
  if (parameter.enum_values?.length) return false
  return parameter.result_type === EAnalysisResultType.TEXT
}

/** There is something to read the result against — a range or a cutoff. */
export function hasNorm(parameter: {
  reference_min: number | null
  reference_max: number | null
  cutoff_value: number | null
}): boolean {
  return (
    parameter.cutoff_value != null ||
    parameter.reference_min != null ||
    parameter.reference_max != null
  )
}
